import { MENU_VALUES } from '@API/workspace';
import useUpdateDefaultWorkspaceMutation from '@API/workspace/useUpdateDefaultWorkspaceMutation';
import { useDispatch } from 'react-redux';
import keyBy from 'lodash/keyBy';
import filter from 'lodash/filter';
import map from 'lodash/map';

import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import useUserPermission from '@shared/api/useUserPermission';
import useOrganizationWorkspaces from '@shared/api/useOrganizationWorkspaces';
import { MANAGEMENT_PERMISSIONS } from '@shared/constants/permissions';
import { SearchInput } from '@design-system/components';

import workspaceActions from '@/actions/workspaceActions';
import { useDeleteWorkspaceMutation } from '@account-settings/api';
import { WorkspacesList } from '@account-settings/components/WorkspacesList';
import { useCurrentOrganization, useWorkspaces } from '@shared/hooks';

import AdminSettingsWorkspaceCard from './AdminSettingsWorkspaceCard';
import SmallLoader from '@shared/components/SmallLoader';
import { CreateWorkspace } from '../../CreateWorkspace';
import { DeleteWorkspaceModal } from '../../DeleteWorkspaceModal';

import './workspaces.scss';

const Workspaces = () => {
  const { organizationId } = useParams();
  const [searchValue, setSearchValue] = useState('');
  const dispatch = useDispatch();

  const { data: orgWorkspaces = [], isLoading } = useOrganizationWorkspaces(
    organizationId
  );

  const [workspaceToRemove, setWorkspaceToRemove] = useState(null);

  const { getPermissionStatus } = useUserPermission();

  const { data: memberAndPublicWorkspaces } = useWorkspaces();
  const updateDefaultWorkspaceMutation = useUpdateDefaultWorkspaceMutation();
  const deleteWorkspaceMutation = useDeleteWorkspaceMutation(organizationId);

  const currentOrganization = useCurrentOrganization();

  const filteredWorkspaces = useMemo(() => {
    if (orgWorkspaces && !memberAndPublicWorkspaces) {
      return orgWorkspaces.filter(ws => {
        return (
          !searchValue ||
          ws.name?.toLowerCase().includes(searchValue.toLowerCase())
        );
      });
    }

    if (!orgWorkspaces) {
      return [];
    }

    const memberIds = keyBy(
      filter(memberAndPublicWorkspaces, { isMember: true }),
      'id'
    );

    const updatedOrgWorkspaces = map(orgWorkspaces, orgWorkspace => ({
      ...orgWorkspace,
      isMember: Boolean(memberIds[orgWorkspace.id])
    }));

    return updatedOrgWorkspaces.filter(ws => {
      return (
        !searchValue ||
        ws.name?.toLowerCase().includes(searchValue.toLowerCase())
      );
    });
  }, [orgWorkspaces, searchValue, memberAndPublicWorkspaces]);

  const handleClickMenu = workspace => option => {
    if (option.value === MENU_VALUES.deleteWorkspace) {
      return setWorkspaceToRemove(workspace);
    }

    if (option.value === MENU_VALUES.default) {
      return updateDefaultWorkspaceMutation.mutate(workspace.id);
    }

    if (option.value === MENU_VALUES.manageUsers) {
      dispatch(
        workspaceActions.setManageWorkspaceModalState({
          open: true,
          workspaceId: workspace?.id,
          workspaceName: workspace?.name
        })
      );
    }
  };

  const handleClickInvite = workspace => () => {
    dispatch(
      workspaceActions.setInviteMembersModalState({
        open: true,
        workspaceId: workspace?.id,
        workspaceName: workspace?.name
      })
    );
  };

  const renderCard = ({ index, style }) => {
    const workspace = filteredWorkspaces[index];

    return (
      <div style={style} key={workspace.id}>
        <AdminSettingsWorkspaceCard
          createdAt={workspace.createdAt}
          createdBy={workspace.createdBy}
          isMember={workspace.isMember}
          isAdmin={currentOrganization?.isAdmin}
          isDefault={workspace.isDefault}
          key={workspace.id}
          onClickExtraMenu={handleClickMenu(workspace)}
          onClickInvite={handleClickInvite(workspace)}
          projectsCount={workspace.projectsCount}
          workspaceId={workspace.id}
          workspaceName={workspace.name}
          workspaceStorageUsage={workspace.storageUsage}
          disabledInviteButton={
            !getPermissionStatus({
              workspaceName: workspace?.name,
              permissionKey:
                MANAGEMENT_PERMISSIONS.INVITE_USERS_FROM_ORGANIZATION
            })
          }
        />
      </div>
    );
  };
  const closeDeleteModal = () => setWorkspaceToRemove(null);
  const handleDeleteConfirm = () => {
    deleteWorkspaceMutation.mutate(workspaceToRemove.id);
    setWorkspaceToRemove(null);
  };

  const noSearchResults =
    !!orgWorkspaces.length && !filteredWorkspaces.length && !!searchValue;

  return (
    <>
      <DeleteWorkspaceModal
        open={Boolean(workspaceToRemove)}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        workspaceName={workspaceToRemove?.name || ''}
      />

      <div className="workspace-tab">
        <div className="page-title">Workspaces</div>

        {isLoading ? (
          <SmallLoader
            primaryMessage="Loading..."
            secondaryMessage="Loading workspaces"
          />
        ) : (
          <>
            <div className="workspaces-controller-header">
              <div>
                <SearchInput
                  placeholder="Search"
                  value={searchValue}
                  onChange={e => setSearchValue(e.target.value)}
                  onClearIconClick={() => setSearchValue('')}
                />
              </div>

              <CreateWorkspace />
            </div>

            <WorkspacesList
              noSearchResults={noSearchResults}
              rowCount={filteredWorkspaces.length}
              renderCard={renderCard}
            />
          </>
        )}
      </div>
    </>
  );
};

export default Workspaces;
